import React from 'react';
import { ArrowRightIcon, CheckIcon, UsersIcon, BookOpenIcon, MicIcon, CalendarIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
interface ServiceLink {
  type: string;
  typeColor: string;
  title: string;
  desc: string;
  path: string;
  cta: string;
}
export function NewToLeadership() {
  const {
    t
  } = useLanguage();
  const signs = t('newToLeadership.signs') as string[];
  const whatWeDoItems = t('newToLeadership.whatWeDoItems') as string[];
  const serviceLinks: ServiceLink[] = [{
    type: t('newToLeadership.courseLabel') as string,
    typeColor: 'bg-coral/10 text-coral',
    title: t('newToLeadership.course1Title') as string,
    desc: t('newToLeadership.course1Desc') as string,
    path: '/services/courses/first-time-manager',
    cta: t('newToLeadership.learnMore') as string
  }, {
    type: t('newToLeadership.workshopLabel') as string,
    typeColor: 'bg-teal/10 text-teal',
    title: t('newToLeadership.workshop1Title') as string,
    desc: t('newToLeadership.workshop1Desc') as string,
    path: '/services/workshops',
    cta: t('newToLeadership.learnMore') as string
  }, {
    type: t('newToLeadership.talkLabel') as string,
    typeColor: 'bg-yellow-100 text-yellow-700',
    title: t('newToLeadership.talk1Title') as string,
    desc: t('newToLeadership.talk1Desc') as string,
    path: '/services/talks',
    cta: t('newToLeadership.learnMore') as string
  }];
  return <PageTransition data-id="element-1796" data-mp="3p56tl5ht1dof">
      <div className="pt-12 pb-24" data-id="element-1797" data-mp="3l5ppogi10old">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1798" data-mp="3l44uconw11mg">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1799" data-mp="3mo6dyatzwl2j" />
          <Animate className="max-w-3xl" data-id="element-1800" data-mp="3cnnpt3veeiet">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1801" data-mp="3cr1q86ki4hjm">
              {t('newToLeadership.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1802" data-mp="33usogl91x6ub">
              {t('newToLeadership.title')}
              <br data-id="element-1803" data-mp="38ox0hix6c529" />
              <span className="text-coral" data-id="element-1804" data-mp="2tyg0m406tn7h">{t('newToLeadership.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1805" data-mp="2yx0xbtg8dor6">
              {t('newToLeadership.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1806" data-mp="2kysw39c5w1a7">
              {t('newToLeadership.intro2')}
            </p>
            <Link to="/contact" data-id="element-1807" data-mp="2p17e5r80ax0f">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1809" data-mp="26bu67isydexl" />} data-id="element-1808" data-mp="26aj1ngroo35f">
                {t('newToLeadership.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs section */}
        <section className="bg-cream py-20 mb-20" data-id="element-1810" data-mp="2fcjwy83cwf4g">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1811" data-mp="2ace3cmdj0ijq">
            <Animate className="mb-12" data-id="element-1812" data-mp="2aaupt3j98lln">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1813" data-mp="2p9exewj6wpld">
                {t('newToLeadership.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1814" data-mp="2ka2907co7ago">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1815" data-mp="2y176b2skfqnu" data-mpi={i}>
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1816" data-mp="2u05185bywkdy">
                    <div className="mt-0.5 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1817" data-mp="37zmvv1by2rv8">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1818" data-mp="331la28n4weg2" />
                    </div>
                    <span className="text-gray-700" data-id="element-1819" data-mp="3n3ftw25corva">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What we work on */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1820" data-mp="3vuzr2jucato5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1821" data-mp="4p6xkghfztk1">
            <Animate data-id="element-1822" data-mp="4rayqqnyiegm">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1823" data-mp="344bs9eg520dn">
                {t('newToLeadership.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1824" data-mp="386ou3rrrle55">
                {t('newToLeadership.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1825" data-mp="3d7cvn7dmdddm">
                {t('newToLeadership.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1826" data-mp="sdzm3rvqiz4m">
              <div className="space-y-3" data-id="element-1827" data-mp="sgvkfhbhvptl">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1828" data-mp="xfyrt8ey684q" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-1829" data-mp="2hulfhwbft1p2">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1830" data-mp="2cxv3bk2r6q8x" />
                    </div>
                    <span className="text-gray-700" data-id="element-1831" data-mp="285bluc0y3ayu">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* Ways to work together */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1832" data-mp="1z66nb5pdvhmo">
          <Animate className="mb-12" data-id="element-1833" data-mp="1u62iicxu8bca">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-1834" data-mp="1u50u76rt9scm">
              {t('newToLeadership.waysTitle')}
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-1835" data-mp="1p929bt5oysgy">{t('newToLeadership.waysSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1836" data-mp="3jla0cvew6p7g">
            {serviceLinks.map((link, i) => {
            const icons = [<BookOpenIcon size={24} className="text-coral" data-id="element-1837" data-mp="3engwwa4z49av" />, <LayersIcon size={24} className="text-teal" data-id="element-1838" data-mp="1cx9ob8i268pq" />, <MicIcon size={24} className="text-yellow-600" data-id="element-1839" data-mp="1cuwy6825cs1l" />];
            return <Animate key={i} delay={i * 0.1} data-id="element-1840" data-mp="1hpt328shf9w4" data-mpi={i}>
                  <Card hoverEffect className="h-full p-6 flex flex-col" data-id="element-1841" data-mp="12zlk7rykbouw">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1842" data-mp="17xnb2z28csgx">
                      {icons[i]}
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${link.typeColor}`} data-id="element-1843" data-mp="1vnr4yepm9duq">
                        {link.type}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg text-charcoal mb-2" data-id="element-1844" data-mp="1mn30obq0yrv1">{link.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1845" data-mp="1qoegxqn0e0qk">{link.desc}</p>
                    <Link to={link.path} data-id="element-1846" data-mp="b7nrm9kjk9jk">
                      <Button variant="outline" size="sm" rightIcon={<ArrowRightIcon size={16} data-id="element-1848" data-mp="ba0khhru84be" />} data-id="element-1847" data-mp="b6l0c0jb4534">
                        {link.cta}
                      </Button>
                    </Link>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1849" data-mp="2q420xheui3x">
          <Animate data-id="element-1850" data-mp="ho64t5gmazk4">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-1851" data-mp="hml5z3n9sugg">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1852" data-mp="cpurgkr466x5">
                <CalendarIcon size={32} className="text-coral" data-id="element-1853" data-mp="3l4wepvfblwr3" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1854" data-mp="3g6utp4yc3ncz">{t('newToLeadership.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1855" data-mp="3uvhjk0ll7xig">{t('newToLeadership.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1856" data-mp="3q2oflexy0t7f">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1858" data-mp="32dlprjt5nu1u" />} data-id="element-1857" data-mp="3q1musir4ksci">
                  {t('newToLeadership.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}